import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Bell,
  FileText,
  BookOpen,
  MessageSquare,
  Users,
  CheckCircle2,
  Clock,
  ChevronRight,
  ChevronLeft,
  Megaphone,
  Heart,
  Send,
  ClipboardCheck,
  AlertTriangle,
  UserX,
  Check
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "react-hot-toast";

const NOTIFICATION_TYPES = {
  // Assignments
  new_assignment: { icon: FileText, color: "bg-blue-100 text-blue-600", label: "Assignment" },
  assignment_submission: { icon: FileText, color: "bg-indigo-100 text-indigo-600", label: "Submission" },
  assignment_reviewed: { icon: CheckCircle2, color: "bg-green-100 text-green-600", label: "Reviewed" },
  
  // Journals
  journal_submission: { icon: BookOpen, color: "bg-amber-100 text-amber-600", label: "Journal" },
  journal_reviewed: { icon: CheckCircle2, color: "bg-green-100 text-green-600", label: "Reviewed" },
  journal_comment: { icon: MessageSquare, color: "bg-teal-100 text-teal-600", label: "Comment" },
  journal_inactive: { icon: UserX, color: "bg-red-100 text-red-600", label: "Inactive Alert" },
  
  // Forum
  forum_post: { icon: Megaphone, color: "bg-purple-100 text-purple-600", label: "Forum" },
  forum_comment: { icon: MessageSquare, color: "bg-purple-100 text-purple-600", label: "Comment" },
  
  // Messages
  direct_message: { icon: Send, color: "bg-sky-100 text-sky-600", label: "Message" },
  group_message: { icon: Users, color: "bg-rose-100 text-rose-600", label: "Group" },
  
  // Check-ins
  checkin_reminder: { icon: ClipboardCheck, color: "bg-orange-100 text-orange-600", label: "Reminder" },
  checkin_submitted: { icon: ClipboardCheck, color: "bg-emerald-100 text-emerald-600", label: "Check-in" },
  
  // Default
  notification: { icon: Bell, color: "bg-slate-100 text-slate-600", label: "Notification" }
};

const FILTER_CATEGORIES = [
  { value: "all",      label: "All",             types: null,  alwaysShow: true },
  { value: "journals", label: "Journals",         types: ["journal_submission", "journal_reviewed", "journal_comment"], alwaysShow: true },
  { value: "assignments", label: "Assignments",   types: ["new_assignment", "assignment_submission", "assignment_reviewed"], alwaysShow: true },
  { value: "checkins", label: "Check-ins",        types: ["checkin_reminder", "checkin_submitted"], alwaysShow: true },
  { value: "messages", label: "Direct Messages",  types: ["direct_message"], alwaysShow: true },
  { value: "inactive", label: "Inactive Alerts",  types: ["journal_inactive"], alwaysShow: false },
  { value: "forum",    label: "Forum",            types: ["forum_post", "forum_comment"], alwaysShow: false },
  { value: "groups",   label: "Group Messages",   types: ["group_message"], alwaysShow: false },
];

const ITEMS_PER_PAGE = 15;

export default function NotificationCenter({ user }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  // Filter out read notifications older than 1 week
  const filterOldReadNotifications = (notifs) => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    return notifs.filter(n => {
      // Keep all unread notifications
      if (!n.is_read) return true;
      
      // For read notifications, only keep if less than 1 week old
      if (n.created_at) {
        const createdDate = new Date(n.created_at);
        return createdDate > oneWeekAgo;
      }
      
      // If no created_at, keep it
      return true;
    });
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/center?limit=500`);
      // Filter out old read notifications
      const filtered = filterOldReadNotifications(response.data);
      setNotifications(filtered);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    const category = FILTER_CATEGORIES.find(c => c.value === activeFilter);
    const idsToMark = new Set(filteredNotifications.map(n => n.id));

    try {
      // Update local state immediately - only the currently filtered notifications
      setNotifications(prev => prev.map(n => idsToMark.has(n.id) ? { ...n, is_read: true } : n));

      if (activeFilter === "all") {
        await axios.post(`${API}/notifications/read-all`);
      } else {
        await axios.post(`${API}/notifications/mark-read`, { types: category?.types });
      }
      toast.success(activeFilter === "all" ? "All notifications marked as read" : `${category.label} marked as read`);
      
      // Refresh to get updated data
      fetchNotifications();
    } catch (error) {
      console.error("Error marking as read:", error);
      toast.error("Failed to mark notifications as read");
      // Revert on error
      fetchNotifications();
    }
  };

  const handleNotificationClick = async (notification, e) => {
    // Prevent default navigation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Mark as read in local state immediately
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
    );
    
    // Mark as read in database
    try {
      if (notification.id?.startsWith("notif_")) {
        // Standard notification
        await axios.post(`${API}/notifications/${notification.id}/read`);
      } else {
        // Other notification types (submissions, journals, messages, etc.)
        await axios.post(`${API}/notifications/mark-read`, {
          id: notification.id,
          type: notification.type
        });
      }
    } catch (error) {
      console.error("Error marking as read:", error);
    }
    
    // Navigate to the link after marking as read
    if (notification.link) {
      setTimeout(() => navigate(notification.link), 100);
    }
  };

  const getNotificationConfig = (type) => {
    return NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.notification;
  };

  // Filter notifications based on active filter
  const filteredNotifications = activeFilter === "all"
    ? notifications
    : notifications.filter(n => {
        const category = FILTER_CATEGORIES.find(c => c.value === activeFilter);
        return category?.types?.includes(n.type);
      });

  // Pagination calculations
  const totalPages = Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedNotifications = filteredNotifications.slice(startIndex, endIndex);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Get UNREAD counts for each filter category
  const getCategoryUnreadCount = (category) => {
    if (!category.types) return notifications.filter(n => !n.is_read).length;
    return notifications.filter(n => category.types.includes(n.type) && !n.is_read).length;
  };

  // Get total counts for each filter category (for showing/hiding filters)
  const getCategoryTotalCount = (category) => {
    if (!category.types) return notifications.length;
    return notifications.filter(n => category.types.includes(n.type)).length;
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Bell className="w-8 h-8 text-amber-600" />
              Notification Center
            </h1>
            <p className="text-slate-600 mt-1">
              Stay updated with all your activities
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Badge className="bg-amber-500 text-white">
                {unreadCount} unread
              </Badge>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={markAllAsRead}
              className="rounded-full"
              data-testid="mark-all-read-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              {activeFilter === "all" ? "Mark all read" : `Mark ${FILTER_CATEGORIES.find(c => c.value === activeFilter)?.label} read`}
            </Button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {FILTER_CATEGORIES.map(category => {
            const totalCount = getCategoryTotalCount(category);
            const unreadCategoryCount = getCategoryUnreadCount(category);
            // Only hide non-core filters when they have 0 items
            if (!category.alwaysShow && totalCount === 0) return null;

            const isActive = activeFilter === category.value;

            return (
              <button
                key={category.value}
                onClick={() => setActiveFilter(category.value)}
                data-testid={`filter-${category.value}`}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all
                  ${isActive
                    ? "bg-slate-800 text-white border-slate-800 shadow-sm"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-800"
                  }`}
              >
                {category.value === "journals"    && <BookOpen className="w-3.5 h-3.5" />}
                {category.value === "assignments" && <FileText className="w-3.5 h-3.5" />}
                {category.value === "checkins"    && <ClipboardCheck className="w-3.5 h-3.5" />}
                {category.value === "messages"    && <Send className="w-3.5 h-3.5" />}
                {category.value === "inactive"    && <AlertTriangle className="w-3.5 h-3.5" />}
                {category.value === "forum"       && <Megaphone className="w-3.5 h-3.5" />}
                {category.value === "groups"      && <Users className="w-3.5 h-3.5" />}
                {category.label}
                {unreadCategoryCount > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold
                    ${isActive ? "bg-amber-400 text-slate-900" : "bg-amber-500 text-white"}`}>
                    {unreadCategoryCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Notifications List */}
        <Card className="border-slate-100">
          <CardContent className="p-0">
            {filteredNotifications.length === 0 ? (
              <div className="py-16 text-center">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No notifications in this category</p>
                <p className="text-sm text-slate-400 mt-1">
                  {activeFilter !== "all" 
                    ? "Try selecting a different filter or 'All'" 
                    : "Updates about assignments, journals, messages, and more will appear here"
                  }
                </p>
              </div>
            ) : (
              <>
                <div className="divide-y divide-slate-100">
                  {paginatedNotifications.map((notification, index) => {
                    const config = getNotificationConfig(notification.type);
                    const IconComponent = config.icon;
                    
                    return (
                      <div
                        key={`${notification.id}-${index}`}
                        onClick={(e) => handleNotificationClick(notification, e)}
                        className={`flex items-start gap-4 p-4 cursor-pointer transition-colors hover:bg-slate-50 ${
                          !notification.is_read ? "bg-amber-50/50" : ""
                        }`}
                        data-testid={`notification-${notification.id}`}
                      >
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.color}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs px-2 py-0">
                              {config.label}
                            </Badge>
                            {!notification.is_read && (
                              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="font-medium text-slate-900 text-sm">
                            {notification.title}
                          </p>
                          <p className="text-slate-600 text-sm mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {notification.created_at ? (
                              formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })
                            ) : (
                              "Recently"
                            )}
                          </p>
                        </div>

                        {/* Arrow */}
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-2" />
                      </div>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-slate-100 bg-slate-50/50">
                    <p className="text-sm text-slate-600">
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredNotifications.length)} of {filteredNotifications.length}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        data-testid="pagination-prev"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      
                      {/* Page numbers */}
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              className={`w-8 h-8 p-0 ${currentPage === pageNum ? "bg-slate-800" : ""}`}
                              onClick={() => setCurrentPage(pageNum)}
                              data-testid={`pagination-page-${pageNum}`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        data-testid="pagination-next"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-100">
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">
                {notifications.filter(n => ["new_assignment","assignment_submission","assignment_reviewed"].includes(n.type)).length}
              </p>
              <p className="text-xs text-slate-500">Assignments</p>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="p-4 text-center">
              <BookOpen className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">
                {notifications.filter(n => ["journal_submission","journal_reviewed","journal_comment"].includes(n.type)).length}
              </p>
              <p className="text-xs text-slate-500">Journals</p>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="p-4 text-center">
              <ClipboardCheck className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">
                {notifications.filter(n => ["checkin_reminder","checkin_submitted"].includes(n.type)).length}
              </p>
              <p className="text-xs text-slate-500">Check-ins</p>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="p-4 text-center">
              <Send className="w-6 h-6 text-sky-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">
                {notifications.filter(n => n.type === "direct_message").length}
              </p>
              <p className="text-xs text-slate-500">Direct Messages</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
